import React from "react";
import { ClientFactory } from "@/main/common/ClientFactory";
import { MetaHandler } from "@/main/common/MetaHandler";
import { createReactMount } from "@/main/react/mountContract";
import SeriesHeaderDisplayer from "@/main/react/series/SeriesHeaderDisplayer";
import { normalizeSeriesReadingState, type SeriesReadingState } from "@/main/react/series/SeriesReadingTypes";
import { getUserFacingSeriesDescription, getUserFacingSeriesTitle } from "@/main/react/series/BookmarksSeriesLocalization";
import { Localizer } from "@/main/react/Types";
import SensitivePageGate from "@/main/react/sensitive/SensitivePageGate";
import { useSensitivePageUnlock } from "@/main/react/sensitive/useSensitivePageUnlock";
import { getPageContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import { SeriesDiscussionList } from "./series-page/SeriesDiscussionListLazy";
import {
    normalizeSeriesDiscussionOrdering,
    type SeriesDiscussionOrderingStyle,
} from "./series-page/seriesDiscussionOrdering";
import { normalizeDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import {
    onDiscussionAddedToSeries,
    onDiscussionRemovedFromSeries,
    onSeriesFollowAdded,
    onSeriesFollowRemoved,
    onSeriesSettingsUpdated,
} from "@/main/common/EventRegistry";
import { useHistoryBackedState } from "@/main/react/page/useHistoryBackedState";
import { PageViewTracker } from "@/main/common/PageViewTracker";
import { cn } from "@/lib/utils";
import { MAIN_PAGE_LIST_WIDTH_CLASS } from "@/main/react/list/MainPageListLayout";

type SeriesRouteState = {
    orderingStyle: SeriesDiscussionOrderingStyle;
};

function buildSeriesPageUrl(
    state: SeriesRouteState,
    location: { pathname: string; search: string; hash: string },
    seriesSlug?: string,
) {
    const params = new URLSearchParams(location.search);
    const orderingStyle = normalizeSeriesDiscussionOrdering(state.orderingStyle);

    if (orderingStyle === "added") {
        params.delete("ordering");
    } else {
        params.set("ordering", orderingStyle);
    }

    const pathname = seriesSlug
        ? `/series/${encodeURIComponent(seriesSlug)}`
        : location.pathname;
    const query = params.toString();
    return `${pathname}${query ? `?${query}` : ""}${location.hash}`;
}

type Props = {
    initialSection?: string;
    seriesPublicId?: string;
    seriesSlug?: string;
    title?: string;
    ownerUsername?: string;
    ownerProfileImageUrl?: string;
    thumbnailUrl?: string;
    imageUrls?: string;
    cardMediaItems?: string;
    createdAt?: string;
    description?: string;
    mainLocale?: string;
    initialDiscussionsCount?: number | null;
    initialFollowersCount?: number | null;
    initiallyOwner?: boolean;
    initiallyFollowing?: boolean;
    privateAccess?: boolean;
    bookmarksCollection?: boolean;
    sensitive?: boolean;
    seriesReadingState?: SeriesReadingState;
    localizer: Localizer;
};

export default function SeriesPage(props: Props) {
    const {
        seriesPublicId,
        seriesSlug,
        title = "",
        ownerUsername,
        ownerProfileImageUrl,
        thumbnailUrl,
        imageUrls,
        cardMediaItems,
        createdAt,
        description,
        mainLocale,
        initialDiscussionsCount,
        initialFollowersCount,
        initiallyOwner = false,
        initiallyFollowing = false,
        privateAccess = false,
        bookmarksCollection = false,
        sensitive = false,
        seriesReadingState,
        localizer
    } = props;

    const seriesApi = React.useMemo(() => ClientFactory.getAjaxSeriesRestControllerApi(), []);
    const hideSensitiveMedia = MetaHandler.getHideSensitiveMedia();
    const [seriesTitle, setSeriesTitle] = React.useState(title);
    const [seriesDescription, setSeriesDescription] = React.useState(description);
    const [seriesThumbnailUrl, setSeriesThumbnailUrl] = React.useState(thumbnailUrl);
    const [seriesImageUrls, setSeriesImageUrls] = React.useState(imageUrls);
    const [seriesCardMediaItems, setSeriesCardMediaItems] = React.useState(cardMediaItems);
    const [seriesMainLocale, setSeriesMainLocale] = React.useState(mainLocale);
    const [seriesDiscussionsCount, setSeriesDiscussionsCount] = React.useState<number | null | undefined>(initialDiscussionsCount);
    const [seriesFollowersCount, setSeriesFollowersCount] = React.useState<number | null | undefined>(initialFollowersCount);
    const [seriesPrivateAccess, setSeriesPrivateAccess] = React.useState(privateAccess);
    const [seriesBookmarksCollection, setSeriesBookmarksCollection] = React.useState(bookmarksCollection);
    const [seriesSensitive, setSeriesSensitive] = React.useState(sensitive);
    const { revealed, reveal, sensitiveMasking } = useSensitivePageUnlock({
        kind: "series",
        id: seriesPublicId,
        isSensitive: seriesSensitive,
    });
    const initialRouteState = React.useMemo<SeriesRouteState>(() => ({
        orderingStyle: normalizeSeriesDiscussionOrdering(),
    }), []);
    const { state: routeState, setState: setRouteState } = useHistoryBackedState<SeriesRouteState>({
        initialState: initialRouteState,
        readState: (location, fallback) => ({
            orderingStyle: normalizeSeriesDiscussionOrdering(new URLSearchParams(location.search).get("ordering") ?? fallback.orderingStyle),
        }),
        buildUrl: (state, location) => buildSeriesPageUrl(state, location, seriesSlug),
        areEqual: (left, right) => left.orderingStyle === right.orderingStyle,
    });
    /**
     * Sorting PUSHES (§13.1): it changes the address, so Back returns to the previous ordering. It used
     * to replace on the "a sort refines what you are already reading" reasoning, which §13.2 retired —
     * the distinction is about intent and the member cannot observe it.
     */
    const handleOrderingStyleChange = React.useCallback((value: string) => {
        setRouteState({ orderingStyle: normalizeSeriesDiscussionOrdering(value) });
    }, [setRouteState]);
    const displayTitle = getUserFacingSeriesTitle({ title: seriesTitle, description: seriesDescription, bookmarksCollection: seriesBookmarksCollection }, localizer);
    const displayDescription = getUserFacingSeriesDescription({ title: seriesTitle, description: seriesDescription, bookmarksCollection: seriesBookmarksCollection }, localizer);

    const displayThumbnailUrl = React.useMemo(() => {
        if (seriesThumbnailUrl) {
            return undefined;
        }
        try {
            const parsed = JSON.parse(seriesImageUrls ?? "[]");
            return Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : undefined;
        } catch {
            return undefined;
        }
    }, [seriesThumbnailUrl, seriesImageUrls]);

    React.useEffect(() => {
        setSeriesTitle(title);
        setSeriesDescription(description);
        setSeriesThumbnailUrl(thumbnailUrl);
        setSeriesImageUrls(imageUrls);
        setSeriesCardMediaItems(cardMediaItems);
        setSeriesMainLocale(mainLocale);
        setSeriesDiscussionsCount(initialDiscussionsCount);
        setSeriesFollowersCount(initialFollowersCount);
        setSeriesPrivateAccess(privateAccess);
        setSeriesBookmarksCollection(bookmarksCollection);
        setSeriesSensitive(sensitive);
    }, [bookmarksCollection, cardMediaItems, description, imageUrls, initialDiscussionsCount, initialFollowersCount, mainLocale, sensitive, privateAccess, thumbnailUrl, title]);

    React.useEffect(() => {
        if (!seriesPublicId) {
            return;
        }

        const unsubscribeSettingsUpdated = onSeriesSettingsUpdated((payload) => {
            if (!payload || payload.seriesPublicId !== seriesPublicId) {
                return;
            }

            setSeriesTitle(payload.title);
            setSeriesDescription(payload.description ?? undefined);
            setSeriesThumbnailUrl(payload.thumbnailUrl ?? undefined);
            setSeriesMainLocale(payload.mainLocale ?? undefined);
            setSeriesPrivateAccess(Boolean(payload.privateAccess));
            setSeriesBookmarksCollection(Boolean(payload.bookmarksCollection));
            setSeriesSensitive(Boolean(payload.sensitive));
        });

        return () => {
            unsubscribeSettingsUpdated();
        };
    }, [seriesPublicId]);

    React.useEffect(() => {
        if (!seriesPublicId) {
            return;
        }

        const unsubscribeDiscussionAdded = onDiscussionAddedToSeries((payload) => {
            if (!payload || payload.seriesPublicId !== seriesPublicId) {
                return;
            }
            setSeriesDiscussionsCount(payload.series?.discussionsCount ?? ((current) => Number(current ?? 0) + 1));
            setSeriesImageUrls((current) => (payload.series as typeof payload.series & { imageUrls?: string | null } | undefined)?.imageUrls ?? current);
            setSeriesCardMediaItems((current) => (payload.series as typeof payload.series & { cardMediaItems?: string | null } | undefined)?.cardMediaItems ?? current);
        });

        const unsubscribeDiscussionRemoved = onDiscussionRemovedFromSeries((payload) => {
            if (!payload || payload.seriesPublicId !== seriesPublicId) {
                return;
            }
            setSeriesDiscussionsCount(payload.series?.discussionsCount ?? ((current) => Math.max(0, Number(current ?? 0) - 1)));
            setSeriesImageUrls((current) => (payload.series as typeof payload.series & { imageUrls?: string | null } | undefined)?.imageUrls ?? current);
            setSeriesCardMediaItems((current) => (payload.series as typeof payload.series & { cardMediaItems?: string | null } | undefined)?.cardMediaItems ?? current);
        });

        const unsubscribeSeriesFollowAdded = onSeriesFollowAdded((payload) => {
            if (!payload || payload.seriesPublicId !== seriesPublicId) {
                return;
            }
            setSeriesFollowersCount((current) => Number(current ?? 0) + 1);
        });

        const unsubscribeSeriesFollowRemoved = onSeriesFollowRemoved((payload) => {
            if (!payload || payload.seriesPublicId !== seriesPublicId) {
                return;
            }
            setSeriesFollowersCount((current) => Math.max(0, Number(current ?? 0) - 1));
        });

        return () => {
            unsubscribeDiscussionAdded();
            unsubscribeDiscussionRemoved();
            unsubscribeSeriesFollowAdded();
            unsubscribeSeriesFollowRemoved();
        };
    }, [seriesPublicId]);

    React.useEffect(() => {
        if (!seriesPublicId) {
            return;
        }

        const tracker = new PageViewTracker({
            errorMessage: "Failed to record series view",
            context: { seriesPublicId },
            trackView: () => ClientFactory.countSeriesView(seriesPublicId),
        });
        tracker.startViewTracking();

        return () => {
            tracker.stopViewTracking();
        };
    }, [seriesPublicId]);

    const handleSaveDiscussionOrder = React.useCallback(async (discussionPublicIds: string[]) => {
        if (!seriesPublicId) {
            throw new Error("seriesPublicId is required to save discussion order");
        }

        await seriesApi.updateDiscussionOrder(seriesPublicId, { discussionPublicIds });
    }, [seriesApi, seriesPublicId]);

    const pageWarningCopy = React.useMemo(
        () => getPageContentWarningCopy(localizer, { isSensitive: seriesSensitive, kind: "series" }),
        [localizer, seriesSensitive],
    );

    /**
     * The header's discussions chip scrolls to the list it counts.
     *
     * The page owns the ref rather than the header querying the DOM for it: both are rendered right
     * here, and a header that went looking for its own page's list would break silently the moment the
     * list moved. Guarded on the ref, because the sensitive gate can render the header with the list
     * still behind an unlock.
     */
    const discussionListRef = React.useRef<HTMLDivElement | null>(null);
    // U44 (`SERIES-19`): while the owner reorders, the mode owns the page. The hero is dimmed and inert,
    // so the docked Save is the only primary and no header control can leave with an unsaved order.
    const [reorderingList, setReorderingList] = React.useState(false);
    const handleDiscussionsCountClick = React.useCallback(() => {
        discussionListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    return (
        <SensitivePageGate
            isSensitive={seriesSensitive}
            hideSensitiveMedia={hideSensitiveMedia}
            revealed={revealed}
            onReveal={reveal}
            localizer={localizer}
            badgeLabel={pageWarningCopy.badgeLabel}
            title={pageWarningCopy.title}
            helperText={pageWarningCopy.helperText}
            revealText={pageWarningCopy.revealText}
            showEnablePreferenceButton={pageWarningCopy.showSensitivePreferenceAction}
            revealButtonDataPlaywrightId="series-page-sensitive-reveal"
            // `SERIES-60`: the gate names what it gates. The title is already in the URL and the
            // document title, so it reveals nothing.
            subjectLabel={displayTitle || undefined}
        >
            {/* The floating action's bottom clearance is NOT reserved here. It lives on the layout's
                default `mainClass` (`layouts/basic_layout.jte`), because the FAB renders on every
                route — see the note in GroupPage and `floating-action-clearance.spec.mjs`. */}
            <div className="flex w-full flex-col items-center px-4 sm:px-6">
                {/* SV-73 (round 29 A12, `SERIES-16`): the header takes the list's own width token
                    (`MAIN_PAGE_LIST_WIDTH_CLASS`), so the two cannot drift apart. `max-w-200` (800px) was
                    a neighbouring value and left the header 30px wider than the list on each side. */}
                <div
                    className={cn(MAIN_PAGE_LIST_WIDTH_CLASS, "pt-2 sm:pt-4", reorderingList && "pointer-events-none opacity-50")}
                    data-playwright-id="series-header-region"
                    data-reordering={reorderingList ? "true" : "false"}
                    aria-hidden={reorderingList ? true : undefined}
                    inert={reorderingList || undefined}
                >
                    <SeriesHeaderDisplayer
                        seriesPublicId={seriesPublicId}
                        seriesSlug={seriesSlug}
                        title={displayTitle}
                        ownerUsername={ownerUsername}
                        ownerProfileImageUrl={ownerProfileImageUrl}
                        thumbnailUrl={seriesThumbnailUrl}
                        displayThumbnailUrl={displayThumbnailUrl}
                        createdAt={createdAt!}
                        description={displayDescription}
                        mainLocale={seriesMainLocale}
                        discussionsCount={seriesDiscussionsCount}
                        followersCount={seriesFollowersCount}
                        initiallyOwner={initiallyOwner}
                        initiallyFollowing={initiallyFollowing}
                        privateAccess={seriesPrivateAccess}
                        bookmarksCollection={seriesBookmarksCollection}
                        sensitive={seriesSensitive}
                        seriesReadingState={seriesReadingState}
                        // `SERIES-25`: at zero posts there is no list to scroll to, so the chip is a caption.
                        onDiscussionsCountClick={Number(seriesDiscussionsCount ?? 0) > 0 ? handleDiscussionsCountClick : undefined}
                        localizer={localizer}
                    />
                </div>

                <div className="mx-auto flex w-full max-w-200 flex-col items-center">
                    <div className="w-full" ref={discussionListRef} data-playwright-id="series-discussion-list-region">
                        <SeriesDiscussionList
                            seriesPublicId={seriesPublicId}
                            seriesSlug={seriesSlug}
                            seriesTitle={displayTitle}
                            ownerUsername={ownerUsername}
                            initiallyFollowing={initiallyFollowing}
                            initiallyOwner={initiallyOwner}
                            bookmarksCollection={seriesBookmarksCollection}
                            seriesReadingState={seriesReadingState}
                            orderingStyle={routeState.orderingStyle}
                            onOrderingStyleChange={handleOrderingStyleChange}
                            onSaveDiscussionOrder={handleSaveDiscussionOrder}
                            onEditModeChange={setReorderingList}
                            sensitiveMasking={sensitiveMasking}
                            localizer={localizer}
                        />
                    </div>
                </div>
            </div>
        </SensitivePageGate>
    );
}

export const mountSeriesPage = createReactMount<Props>({
    name: "mountSeriesPage",
    getProps: ({ dataset, parsed, localizer }) => ({
        initialSection: dataset.section ?? "discussions",
        seriesPublicId: dataset.seriesPublicId ?? dataset.seriesPublicid ?? undefined,
        seriesSlug: dataset.seriesSlug ?? dataset.seriesslug ?? undefined,
        title: dataset.title ?? dataset.seriesTitle ?? "",
        ownerUsername: normalizeDisplayUsername(dataset.ownerUsername) ?? normalizeDisplayUsername(dataset.ownerusername) ?? undefined,
        ownerProfileImageUrl: dataset.ownerProfileImageUrl ?? dataset.ownerprofileimageurl ?? undefined,
        thumbnailUrl: dataset.thumbnailUrl ?? dataset.thumbnailurl ?? "",
        imageUrls: dataset.imageUrls ?? dataset.imageurls ?? "[]",
        cardMediaItems: dataset.cardMediaItems ?? dataset.cardmediaitems ?? "[]",
        mainLocale: dataset.mainLocale ?? dataset.mainlocale ?? "",
        initialDiscussionsCount: dataset.discussionsCount ? Number(dataset.discussionsCount) : null,
        initialFollowersCount: dataset.followersCount ? Number(dataset.followersCount) : null,
        createdAt: dataset.createdAt ?? dataset.createdat ?? "",
        description: dataset.description ?? "",
        initiallyOwner: dataset.initiallyOwner === "true",
        initiallyFollowing: dataset.initiallyFollowing === "true",
        privateAccess: dataset.privateAccess === "true",
        bookmarksCollection: dataset.bookmarksCollection === "true",
        sensitive: dataset.sensitive === "true",
        seriesReadingState: normalizeSeriesReadingState(parsed.seriesReadingState),
        localizer,
    }),
    render: (props) => <SeriesPage {...props} />,
});
